import { readTrainingGroundDraft } from "@app/training-ground/utils/training-ground-storage";
import { getTrainingGroundHistorySubjectLabel } from "@app/training-ground/utils/training-ground-state";
import { URLS } from "@core/shared/utils";
import { platformFeaturesAtom } from "@framework/shared/atoms";
import { trans, plainTrans } from "@mongez/localization";
import { Link } from "@remix-run/react";
import {
  ArrowRight,
  BrainCircuit,
  PlayCircle,
  RotateCw,
  Sparkles,
  Target,
} from "lucide-react";
import { useEffect, useState } from "react";
import {
  getTrainingGroundHomeHref,
  resolveTrainingGroundHomeGuidance,
  type TrainingGroundHomeData,
} from "../utils/training-ground-home";

const guidanceIcons = {
  resume: PlayCircle,
  review: RotateCw,
  targetSubject: Target,
  adaptive: Sparkles,
  start: BrainCircuit,
};

/**
 * One fail-soft Training Ground next-action card shared by both home themes.
 * It links into practice and never auto-starts or overrides resume handling.
 */
export function TrainingGroundHomeCard({
  data,
}: {
  data?: TrainingGroundHomeData;
}) {
  const platformFeatures = platformFeaturesAtom.useValue();
  const [hasDraft, setHasDraft] = useState(false);

  useEffect(() => {
    if (!data) return;

    setHasDraft(Boolean(readTrainingGroundDraft(window.localStorage, data.studentId)));
  }, [data]);

  if (!data || platformFeatures.trainingGround !== true) return null;

  const guidance = resolveTrainingGroundHomeGuidance({
    hasDraft,
    mistakes: data.mistakes,
    analytics: data.analytics,
  });
  const Icon = guidanceIcons[guidance.kind];
  const subjectLabel =
    guidance.kind === "targetSubject"
      ? getTrainingGroundHistorySubjectLabel(guidance.subject)
      : undefined;
  const title =
    guidance.kind === "targetSubject"
      ? trans("home.trainingGround.targetSubjectTitle", {
          subject: subjectLabel || trans("home.trainingGround.subjectFallback"),
        })
      : trans(`home.trainingGround.${guidance.kind}Title`);
  const description =
    guidance.kind === "review"
      ? trans("home.trainingGround.reviewDescription", {
          count: guidance.dueCount,
        })
      : guidance.kind === "targetSubject"
        ? trans("home.trainingGround.targetSubjectDescription", {
            accuracy: guidance.accuracy,
          })
        : trans(`home.trainingGround.${guidance.kind}Description`);

  return (
    <section
      aria-label={plainTrans("home.trainingGround.ariaLabel")}
      className="relative overflow-hidden rounded-3xl border border-cyan-200 bg-gradient-to-br from-cyan-50 via-white to-emerald-50 p-5 shadow-sm dark:border-cyan-900/70 dark:from-cyan-950/40 dark:via-slate-950 dark:to-emerald-950/30 sm:p-6"
    >
      <div
        aria-hidden="true"
        className="absolute -right-16 -top-20 h-48 w-48 rounded-full bg-cyan-200/40 blur-3xl dark:bg-cyan-800/20"
      />
      <div className="relative flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-start gap-4">
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-cyan-600 text-white shadow-sm shadow-cyan-600/25">
            <Icon size={23} aria-hidden="true" />
          </span>
          <div>
            <p className="text-xs font-black uppercase tracking-[0.18em] text-cyan-700 dark:text-cyan-300">
              {trans("home.trainingGround.eyebrow")}
            </p>
            <h2 className="mt-1 text-xl font-black text-slate-950 dark:text-white sm:text-2xl">
              {title}
            </h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              {description}
            </p>
            <div className="mt-3 flex flex-wrap gap-2 text-xs font-bold">
              {data.analytics && data.analytics.completedRounds > 0 && (
                <span className="rounded-full bg-cyan-100 px-3 py-1 text-cyan-800 dark:bg-cyan-950/70 dark:text-cyan-200">
                  {trans("home.trainingGround.accuracy", {
                    accuracy: data.analytics.accuracy,
                  })}
                </span>
              )}
              {data.mistakes && data.mistakes.dueCount > 0 && (
                <span className="rounded-full bg-amber-100 px-3 py-1 text-amber-800 dark:bg-amber-950/70 dark:text-amber-200">
                  {trans("home.trainingGround.due", {
                    count: data.mistakes.dueCount,
                  })}
                </span>
              )}
            </div>
          </div>
        </div>

        <Link
          to={getTrainingGroundHomeHref(URLS.trainingGround, guidance)}
          className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-xl bg-cyan-700 px-5 py-2.5 text-sm font-black text-white shadow-sm transition hover:bg-cyan-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2 dark:bg-cyan-500 dark:text-slate-950 dark:hover:bg-cyan-400"
        >
          {trans(`home.trainingGround.${guidance.kind}Action`)}
          <ArrowRight className="rtl:rotate-180" size={17} aria-hidden="true" />
        </Link>
      </div>
    </section>
  );
}
